import { useState } from 'react';
import { Link } from 'react-router-dom';

const allFaqs = [
  {
    category: 'General',
    faqs: [
      { q: 'What is Art Den Studio?', a: 'Art Den Studio is an art studio in Nazareth, PA owned by Kelsey. The studio offers guided art classes, private parties, creative events, and open studio sessions for kids and adults throughout the Lehigh Valley. All supplies are provided for every session.' },
      { q: 'Where is Art Den Studio located?', a: 'Art Den Studio is located at 37 E Belvidere Street, Nazareth, PA 18064. The studio is easily accessible from Bethlehem, Easton, Allentown, Northampton, and communities across Northampton County and the Lehigh Valley.' },
      { q: 'How do I contact Art Den Studio?', a: 'You can reach Art Den Studio by phone at 610-340-0314 or by email at info@artdenstudio.com. You can also find upcoming sessions and book online through the studio calendar at artdenstudio.com.' },
      { q: 'What are the studio hours?', a: 'Art Den Studio operates on an event and class-based schedule. Hours vary depending on monthly bookings and sessions. Check the calendar at artdenstudio.com for current availability, or call 610-340-0314 for private booking inquiries.' },
    ],
  },
  {
    category: 'Art Classes',
    faqs: [
      { q: 'Do I need art experience to take a class?', a: 'Not at all. Every class at Art Den Studio is designed for beginners and experienced painters alike. Kelsey walks you through each step of the painting at a comfortable, relaxed pace. No prior experience is ever required.' },
      { q: 'What supplies do I need to bring?', a: 'Nothing! All art supplies — including canvas, paint, brushes, and any other materials needed for the session — are provided. Just bring yourself and your enthusiasm. Everything else is taken care of.' },
      { q: 'Are there art classes for kids?', a: 'Yes! Art Den Studio offers classes specifically designed for younger artists. Kids\' classes are age-appropriate, instructor-led by Kelsey, and created to be fun, engaging, and encouraging for children of all artistic backgrounds.' },
      { q: 'How long does an art class take?', a: 'Session lengths vary depending on the type of class. Most guided painting sessions run approximately 2 hours. Check the Art Den Studio calendar for specific session durations and timing for upcoming classes.' },
    ],
  },
  {
    category: 'Parties & Events',
    faqs: [
      { q: 'Does Art Den Studio host birthday parties?', a: 'Yes! Birthday parties are one of our most popular offerings. We host fully private studio parties for both kids and adults. Kelsey leads a fun, guided painting session for your group. All supplies are included and the studio is reserved exclusively for your event.' },
      { q: 'Can I book a private bachelorette party?', a: 'Absolutely. Private bachelorette parties are a fantastic fit for Art Den Studio. You and your group get exclusive studio access, guided art instruction from Kelsey, and a truly unique experience that makes for amazing memories and take-home artwork.' },
      { q: 'Do you host corporate team events?', a: 'Yes. Art Den Studio is a great venue for corporate team bonding events. Creative sessions provide a relaxed, fun way for teams to connect outside the office. Contact us at 610-340-0314 or info@artdenstudio.com to discuss group sizes and scheduling.' },
      { q: 'How do I book a private party?', a: 'To book a private party, contact Art Den Studio by phone at 610-340-0314 or email info@artdenstudio.com. You can also initiate a booking through the studio calendar online. We\'ll work with you on dates, group size, and any customizations for your event.' },
    ],
  },
  {
    category: 'Pricing & Booking',
    faqs: [
      { q: 'How much do art classes cost?', a: 'Pricing varies by session type, duration, and group size. Individual class prices and party package rates are available on the Art Den Studio calendar and booking page. Contact the studio at 610-340-0314 or info@artdenstudio.com for detailed pricing information.' },
      { q: 'Do you offer gift cards?', a: 'Yes! Art Den Studio gift cards are available for purchase and make a perfect gift for any occasion. Gift cards can be bought online at app.getoccasion.com. They\'re ideal for birthdays, holidays, or anyone who loves to be creative.' },
      { q: 'Can I cancel or reschedule my booking?', a: 'For cancellation and rescheduling policies, please contact Art Den Studio directly at 610-340-0314 or info@artdenstudio.com. We\'re happy to work with you on changes to your booking when given sufficient notice.' },
    ],
  },
];

const FAQsPage = () => {
  const [activeCategory, setActiveCategory] = useState('General');
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const currentFaqs = allFaqs.find(g => g.category === activeCategory)?.faqs || [];

  return (
    <main>
      {/* HERO */}
      <section className="relative bg-artden-dark py-32 md:py-40 overflow-hidden">
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'radial-gradient(circle, #d4693a 1px, transparent 1px)', backgroundSize: '48px 48px' }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-artden-coral/20 border border-artden-coral/40 text-artden-coral px-4 py-1.5 rounded-full text-xs font-outfit font-semibold tracking-widest uppercase mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-question-line"></i></span>
            Frequently Asked Questions
          </div>
          <h1 className="font-cormorant text-5xl md:text-7xl font-light text-white leading-tight mb-5">
            Got Questions?<br />
            <em className="font-medium text-artden-coral">We've Got Answers.</em>
          </h1>
          <p className="font-outfit text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">
            Everything you need to know about Art Den Studio's classes, parties, booking, and what to expect when you visit us in Nazareth, PA.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="bg-artden-light py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-10 justify-center">
            {allFaqs.map((group) => (
              <button
                key={group.category}
                onClick={() => { setActiveCategory(group.category); setOpenFaq(null); }}
                className={`px-5 py-2.5 rounded-full text-sm font-outfit font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === group.category
                    ? 'bg-artden-coral text-white'
                    : 'bg-artden-cream text-artden-muted hover:text-artden-dark'
                }`}
              >
                {group.category}
              </button>
            ))}
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-3">
            {currentFaqs.map((faq, i) => {
              const key = `${activeCategory}-${i}`;
              return (
                <div key={key} className="border border-artden-dark/10 rounded-xl overflow-hidden bg-white">
                  <button
                    className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left cursor-pointer"
                    onClick={() => setOpenFaq(openFaq === key ? null : key)}
                  >
                    <h3 className="font-outfit text-artden-dark text-sm md:text-base font-medium leading-snug">{faq.q}</h3>
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-artden-cream shrink-0">
                      <i className={`text-artden-coral text-sm ${openFaq === key ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </span>
                  </button>
                  {openFaq === key && (
                    <div className="px-5 md:px-6 pb-5 md:pb-6 border-t border-artden-cream pt-4">
                      <p className="font-outfit text-artden-muted text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Still have questions */}
          <div className="mt-14 bg-artden-dark rounded-2xl p-8 md:p-12 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-artden-coral/20 rounded-full mx-auto mb-5">
              <i className="ri-customer-service-2-line text-artden-coral text-2xl"></i>
            </div>
            <h2 className="font-cormorant text-3xl font-medium text-white mb-3">Still Have Questions?</h2>
            <p className="font-outfit text-white/60 text-sm mb-7 max-w-md mx-auto">
              Reach out to Kelsey and the Art Den Studio team directly — we're happy to help with anything.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="tel:6103400314" className="flex items-center gap-2 bg-artden-coral hover:bg-artden-coral/90 text-white font-semibold px-6 py-3 rounded-full text-sm transition-all whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line"></i></span>
                Call 610-340-0314
              </a>
              <a href="mailto:info@artdenstudio.com" className="flex items-center gap-2 border border-white/20 text-white/80 hover:text-white font-medium px-6 py-3 rounded-full text-sm transition-all whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-line"></i></span>
                info@artdenstudio.com
              </a>
              <Link to="/location" className="flex items-center gap-2 text-white/50 hover:text-artden-coral text-sm font-medium transition-colors whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-2-line"></i></span>
                Visit Us in Nazareth
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default FAQsPage;
